<template>
    <MainContainer>
        <template v-slot:sidebar>&nbsp;</template>
        <div class="mv-probenplan">
            <h2>Probenplan</h2>
            <DateSwitcher :initial="storedSelectedDate" @change="changeDate" />
            <Table
                bordered
                small
                :responsive="true"
                :fields="fields"
                :items="items"
                :isBusy="isBusy"
                :showFilter="true"
                selfStripedKey="day"
                :selfStriped="true"
                busyText="Lädt Probenplan..."
                emptyText="Keine Informationen zum Probenplan gefunden"
            >
                <template v-slot:start="{ row: row }">
                    <template v-if="row.item.full_day"
                        ><Tag>ganztägig</Tag></template
                    ><template v-else>
                        <template v-if="row.item.farbe">
                            <span :style="'color: #' + row.item.farbe">
                                {{ row.value
                                }}<template v-if="row.item.end">
                                    -
                                    {{ row.item.end }}
                                </template>
                            </span>
                        </template>
                        <template v-else
                            >{{ row.value
                            }}<template v-if="row.item.end">
                                -
                                {{ row.item.end }}
                            </template></template
                        >
                    </template></template
                >

                <template v-slot:sopran="{ row: row }">
                    <template v-if="row.value"
                        >S ({{ row.item.proben_zaehler_sopran }})</template
                    >
                    <template v-else>{{ "" }}</template>
                </template>

                <template v-slot:alt="{ row: row }">
                    <template v-if="row.value"
                        >A ({{ row.item.proben_zaehler_alt }})</template
                    >
                    <template v-else>{{ "" }}</template>
                </template>

                <template v-slot:tenor="{ row: row }">
                    <template v-if="row.value"
                        >T ({{ row.item.proben_zaehler_tenor }})</template
                    >
                    <template v-else>{{ "" }}</template>
                </template>

                <template v-slot:bass="{ row: row }">
                    <template v-if="row.value"
                        >B ({{ row.item.proben_zaehler_bass }})</template
                    >
                    <template v-else>{{ "" }}</template>
                </template>

                <template v-slot:werk="{ row: row }">
                    <template v-if="row.item.farbe">
                        <span :style="'color: #' + row.item.farbe">
                            {{ row.value }}
                        </span>
                    </template>
                    <template v-else>{{ row.value }}</template>
                </template>

                <template v-slot:info="{ row: row }">
                    <template v-if="row.item.farbe">
                        <span :style="'color: #' + row.item.farbe">
                            {{ row.value }}
                        </span>
                    </template>
                    <template v-else>{{ row.value }}</template>
                    {{ getVoiceTitles(row) }}
                </template>

                <template v-slot:ort="{ row: row }">
                    <template v-if="row.item.farbe">
                        <span :style="'color: #' + row.item.farbe">
                            {{ row.value }}
                        </span>
                    </template>
                    <template v-else>{{ row.value }}</template>
                </template>

                <template v-slot:notiz="{ row: row }">
                    <template v-if="row.item.farbe">
                        <span :style="'color: #' + row.item.farbe">
                            {{ row.value }}
                        </span>
                    </template>
                    <template v-else>{{ row.value }}</template>
                </template>

                <template v-slot:kuerzelDa="{ row: row }">
                    <template v-if="row.value">
                        {{ row.value }} ({{ row.item.probenZaehlerDa }})
                    </template>
                </template>

                <template v-slot:geaendert="{ row: row }">
                    <template v-if="row.value === 1">
                        <font-awesome-icon icon="exclamation-triangle" />
                    </template>
                    <template v-else>{{ "" }}</template>
                </template>

                <template v-slot:actions="{ row: row }">
                    <b-button
                        v-if="row.day && checkRight('write')"
                        :size="size"
                        @click="dataCreate(row)"
                        variant="link"
                        class="mv-table-action-btn mr-1"
                        >Hinzufügen</b-button
                    >
                    <b-button
                        v-if="row.id && checkRight('write')"
                        :size="size"
                        @click="dataSelected(row)"
                        variant="link"
                        class="mv-table-action-btn mr-1"
                        >Bearbeiten</b-button
                    >
                    <b-button
                        v-if="row.id && checkRight('delete')"
                        :size="size"
                        @click="dataDelete(row)"
                        variant="link"
                        class="text-danger mv-table-action-btn"
                        >Löschen</b-button
                    >
                </template>
            </Table>
            <!-- Info modal -->
            <b-modal
                id="editModal"
                ref="editModal"
                :title="selected.title"
                size="xl"
                scrollable
                ok-title="Speichern"
                cancel-title="Abbrechen"
                :busy="selected.busy"
                @ok="dataProvide"
                @hide="dataHide"
            >
                <ProbenplanDetail
                    v-model="selected.data"
                    :probentypen="probentypSelect"
                    :validationResponse="validationResponse"
                    :parentRights="rights"
                />
                <template slot="modal-ok">
                    <template v-if="!selected.busy">Speichern</template>
                    <template v-else>
                        <Spinner small variant="light" />
                    </template>
                </template>
            </b-modal>

            <b-modal
                id="removeModal"
                ref="removeModal"
                size="sm"
                title="Löschen bestätigen"
                ok-variant="danger"
                centered
                :busy="selected.busy"
                @ok="dataProvide"
                @hide="dataHide"
            >
                Datensatz wirklich löschen?
                <template slot="modal-ok">
                    <template v-if="!selected.busy">Löschen</template>
                    <template v-else>
                        <Spinner small variant="light" />
                    </template>
                </template>
                <template slot="modal-cancel">Abbrechen</template>
            </b-modal>
        </div>
    </MainContainer>
</template>

<script>
import Toaster from "@/mixins/Toaster/Toaster";
import Rights from "@/mixins/Rights/Rights";
import MainContainer from "@/components/MainContainer/MainContainer";
import { ApiFactory } from "@/api/ApiFactory";
import Table from "@/components/Table/Table";
import DateSwitcher from "@/components/DateSwitcher/DateSwitcher";
import ProbenplanDetail from "@/components/ProbenplanDetail/ProbenplanDetail";
import Spinner from "@/components/Spinner/Spinner";
import Tag from "@/components/Tag/Tag";

import "./Probenplan.scss";

const ProbenplanApi = ApiFactory.get("probenplan");
const ProbentypApi = ApiFactory.get("probentyp");

export default {
    name: "Probenplan",
    mixins: [Toaster, Rights],
    components: {
        MainContainer,
        Table,
        DateSwitcher,
        ProbenplanDetail,
        Spinner,
        Tag
    },
    data() {
        return {
            fields: [
                {
                    key: "day",
                    label: "Tag",
                    sortable: false
                },
                {
                    key: "weekday",
                    label: "",
                    sortable: false
                },
                {
                    key: "start",
                    label: "Zeit",
                    sortable: false
                },
                {
                    key: "pk_bezeichnung",
                    label: "PTyp",
                    sortable: false
                },
                {
                    key: "tenor",
                    label: "T",
                    sortable: false,
                    tdClass: "no-wrap",
                    class: "text-center"
                },
                {
                    key: "bass",
                    label: "B",
                    sortable: false,
                    tdClass: "no-wrap",
                    class: "text-center"
                },
                {
                    key: "werk",
                    label: "Werk",
                    sortable: false
                },
                {
                    key: "info",
                    label: "Info",
                    sortable: false
                },
                {
                    key: "ort",
                    label: "Ort",
                    sortable: false
                },
                {
                    key: "notiz",
                    label: "Notiz",
                    sortable: false
                },
                {
                    key: "sopran",
                    label: "S",
                    sortable: false,
                    tdClass: "no-wrap",
                    class: "text-center"
                },
                {
                    key: "alt",
                    label: "A",
                    sortable: false,
                    tdClass: "no-wrap",
                    class: "text-center"
                },
                {
                    key: "geaendert",
                    label: "!",
                    sortable: false
                },
                {
                    key: "actions",
                    label: "Funktionen"
                }
            ],
            data: [],
            isBusy: false,
            probentypen: [],
            selected: {
                data: null,
                action: null,
                title: "",
                busy: false
            },
            selectedDate: null,
            validationResponse: null
        };
    },
    created() {
        this.fetch(this.storedSelectedDate);
        this.fetchProbentypen();
    },
    computed: {
        size() {
            const settings = this.$store.getters.getSettings;
            return settings.size;
        },
        storedSelectedDate() {
            const settings = this.$store.getters.getSettings;
            if (settings.persistentFilterStoring) {
                const pages = this.$store.getters.getPages;
                if (pages.probenplan) {
                    const probenplan = pages.probenplan;
                    return probenplan.selectedDate;
                }
                return null;
            } else {
                return this.selectedDate;
            }
        },
        items() {
            return this.process(this.data);
        },
        probentypSelect() {
            var select = [
                {
                    text: "Ohne Probentyp",
                    value: null
                }
            ];

            select = select.concat(
                this.probentypen.map(item => {
                    const text = item.bezeichnung + " - " + item.kuerzel;
                    return {
                        value: item.id,
                        text: text
                    };
                })
            );
            return select;
        }
    },
    methods: {
        async fetch(selectedDate) {
            this.isBusy = true;
            var args = {};
            if (selectedDate) {
                Object.assign(args, { start: selectedDate });
            }
            this.selectedDate = selectedDate
                ? selectedDate
                : this.$moment(new Date())
                      .startOf("month")
                      .format("YYYY-MM-DD");

            const response = await ProbenplanApi.get(args);
            if (!response.ok) {
                // todo: Errorhandling
            }
            this.data = response.json.collection;
            this.registerRights(response);
            this.isBusy = false;
        },
        async fetchProbentypen() {
            const response = await ProbentypApi.get();
            if (response.ok) {
                const probentypen = response.json.collection;
                this.probentypen = probentypen;
            }
        },
        process(data) {
            const dateArr = [];
            var selectedDate = this.selectedDate;
            if (!selectedDate) {
                selectedDate = new Date(Date.now());
                selectedDate.setDate(1);
            }
            var startDate = this.$moment(selectedDate);
            var endDate = this.$moment(startDate).add(1, "month");
            while (startDate.isBefore(endDate)) {
                var isFirstRowSet = false;
                var element = {
                    day: this.$moment(startDate).format("DD.MM."),
                    weekday: this.$moment(startDate).format("dd")
                };

                var dataElements = [];
                if (data) {
                    dataElements = data.filter(el => {
                        return this.$moment(startDate).isSame(el.datum, "day");
                    });
                }

                if (dataElements.length > 0) {
                    dataElements.forEach(el => {
                        el.full_day = el.full_day == 1;

                        var stimmen = [];
                        if (el.sopran === 1) {
                            stimmen.push("sopran");
                        }
                        if (el.alt === 1) {
                            stimmen.push("alt");
                        }
                        if (el.tenor === 1) {
                            stimmen.push("tenor");
                        }
                        if (el.bass === 1) {
                            stimmen.push("bass");
                        }
                        el.stimmen = stimmen;

                        if (!isFirstRowSet) {
                            Object.assign(element, el);
                            dateArr.push(element);

                            isFirstRowSet = true;
                        } else {
                            dateArr.push(el);
                        }
                    });
                } else {
                    dateArr.push(element);
                }

                startDate.add(1, "day");
            }
            return dateArr;
        },
        changeDate(selectedDate) {
            this.$store.dispatch("pages", {
                probenplan: {
                    selectedDate: selectedDate
                }
            });
            this.fetch(selectedDate);
            this.$forceUpdate();
        },
        dataCreate(selectedItem) {
            var date = selectedItem.datum;
            if (!date) {
                date = "";
                const selectedDate = this.$moment(
                    this.selectedDate,
                    "YYYY-MM-DD"
                );
                date += selectedDate.format("YYYY");
                date += "-" + selectedDate.format("MM");
                date += "-" + selectedItem.day.split(".")[0];
            }
            const newObj = {
                datum: date,
                day: selectedItem.day,
                weekday: selectedItem.weekday,
                farbe: "000000",
                pk_typ: null
            };
            this.dataSet(newObj, "new", "Termin hinzufügen");
            this.$refs["editModal"].show();
        },
        dataSelected(selectedItem) {
            if (!selectedItem) {
                selectedItem = this.items.find(item => {
                    return item.id == index;
                });
            }
            this.dataSet(selectedItem, "edit", "Termin bearbeiten");
            this.$refs["editModal"].show();
        },
        dataDelete(selectedItem) {
            if (!selectedItem) {
                selectedItem = this.items.find(item => {
                    return item.id == index;
                });
            }
            this.dataSet(selectedItem, "remove", "Datensatz löschen");
            this.$refs.removeModal.show();
        },
        async dataProvide(evt) {
            evt.preventDefault();

            const selectedItem = this.selected.data;

            if (selectedItem) {
                // TODO: Validation

                this.selected.busy = true;
                var response = null;

                if (this.selected.action === "new") {
                    response = await ProbenplanApi.post(selectedItem);
                } else if (this.selected.action === "remove") {
                    response = await ProbenplanApi.delete(selectedItem.id);
                } else {
                    response = await ProbenplanApi.put(
                        selectedItem.id,
                        selectedItem
                    );
                }

                if (response.ok) {
                    this.fetch(this.storedSelectedDate);
                    this.$refs.editModal.hide();
                    this.$refs.removeModal.hide();
                    this.makeToast(
                        "Erfolgreich gespeichert",
                        "Die Änderungen wurden erfolgreich in der Datenbank gespeichert"
                    );
                } else {
                    // TODO: Error handling
                    this.validationResponse = response;
                }

                this.selected.busy = false;
            }
        },
        dataHide(evt) {
            if (evt.trigger !== "ok") {
                this.dataSet();
            }
        },
        dataSet(data = null, action = null, title = "") {
            this.selected = {
                data: { ...data },
                action: action,
                title: title,
                busy: false
            };
        },
        getVoiceTitles(row) {
            var str = "";
            var cnt = 0;
            var first = false;
            if (row.item.sopran == 1) {
                var substr = "Sopran";
                if (!first) {
                    first = true;
                } else {
                    substr = "+ " + substr;
                }
                str += substr;
                cnt++;
            }
            if (row.item.alt == 1) {
                var substr = "Alt";
                if (!first) {
                    first = true;
                } else {
                    substr = "+ " + substr;
                }
                str += substr;
                cnt++;
            }
            if (row.item.tenor == 1) {
                var substr = "Tenor";
                if (!first) {
                    first = true;
                } else {
                    substr = " + " + substr;
                }
                str += substr;
                cnt++;
            }
            if (row.item.bass == 1) {
                var substr = "Bass";
                if (!first) {
                    first = true;
                } else {
                    substr = "+ " + substr;
                }
                str += substr;
                cnt++;
            }
            if (cnt === 4) {
                str = "gemeinsam";
            }
            if (str !== "") {
                str = "(" + str + ")";
            }
            return str;
        }
    }
};
</script>
